import { StatusIconName } from '@dynamic/ui';

import { Dictionary } from '#/service/utils';

export enum StorageKeys {
  FALLBACK_CONFIG = 'fallback-config',
  DEVICE_ID = 'device-id',
  TERMS = 'T&C',
  LOGIN_EMAIL = 'login-email',
  SUBTITLE_TRACK = 'subtitle-track',
  AUDIO_TRACK = 'audio-track',
  AUTHENTICATION = 'authentication',
  LOCALE = 'locale',
  TVE_MVPD = 'tve-mvpd',
  TVE_CODE = 'tve-code',
  // Storage keys used for development purposes only
  DEVTOOLS_ADS = 'dev_settings_ads',
  DEVTOOLS_PLAYER_CONTROLS = 'dev_settings_player_controls',
  DEVTOOLS_CONSOLE_OVERRIDE = 'dev_settings_console_override',
}

export enum AppStatus {
  Active = 'ACTIVE',
  Error = 'ERROR',
  GeoBlocked = 'GEOBLOCKED',
  Playback = 'PLAYBACK',
  Maintenance = 'MAINTENANCE',
  Configuration = 'CONFIGURATION',
  AccessDenied = 'ACCESSDENIED',
  Success = 'SUCCESS',
  UpdateRequired = 'UPDATEREQUIRED',
  Loading = 'LOADING',
}

export const AppStatusIcon: Dictionary<AppStatus, StatusIconName | null> = {
  [AppStatus.Active]: null,
  [AppStatus.Error]: 'generic_error',
  [AppStatus.GeoBlocked]: 'geo_restriction',
  [AppStatus.Playback]: null,
  [AppStatus.Maintenance]: 'maintenance',
  [AppStatus.Configuration]: 'configuration_error',
  [AppStatus.AccessDenied]: 'login_required',
  [AppStatus.Success]: 'success',
  [AppStatus.UpdateRequired]: 'update_required',
  [AppStatus.Loading]: 'loading',
};

export enum ScrollBehaviour {
  Fixed = 'FIXED',
  Hidden = 'HIDDEN',
}

export enum MenuBackgroundStyle {
  Gradient = 'GRADIENT',
  Opaque = 'OPAQUE',
}

export enum MenuFocusStyle {
  Box = 'BOX',
  Line = 'LINE',
  None = 'NONE',
}

export enum NavigationType {
  Page = 'PAGE',
  Internal = 'INTERNAL',
  External = 'EXTERNAL',
}

export enum ButtonVariant {
  Primary = 'PRIMARY',
  Secondary = 'SECONDARY',
  Ghost = 'GHOST',
  Promo = 'PROMO',
}

export enum ButtonVariantConfig {
  Primary = 'primary',
  Secondary = 'secondary',
  Ghost = 'ghost',
  Promo = 'promo',
}

export enum ButtonShape {
  Pill = 'PILL',
  Rounded = 'ROUNDED',
  Squared = 'SQUARED',
}

export enum ScrollState {
  Hidden = 'hidden',
  Auto = 'auto',
}

// intentionally uppercase, to ease code generation
export enum IconSubType {
  NORMAL = 'NORMAL',
  PG = 'PG',
}

export enum NetworkStatus {
  ONLINE = 'online',
  OFFLINE = 'offline',
}

export enum FormFactors {
  DESKTOP = 'desktop',
  TABLET = 'tablet',
  MOBILE = 'mobile',
  TV = 'tv',
}

export enum CornerRadius {
  NONE = 'NONE',
  SMALL = 'SMALL',
  MEDIUM = 'MEDIUM',
  LARGE = 'LARGE',
}

export const cornerRadiusPx: Dictionary<CornerRadius, number> = {
  [CornerRadius.NONE]: 0,
  [CornerRadius.SMALL]: 5,
  [CornerRadius.MEDIUM]: 10,
  [CornerRadius.LARGE]: 15,
};

export enum BreakpointKeys {
  XS = 'xs',
  SM = 'sm',
  MD = 'md',
  LG = 'lg',
  XL = 'xl',
  XXL = 'xxl',
}

export enum FormInputVariants {
  CHECKBOX = 'checkbox',
  EMAIL = 'email',
  PASSWORD = 'password',
  SELECT = 'select',
  TEXT = 'text',
  NUMBER = 'number',
  BUTTON = 'button',
  SUBMIT = 'submit',
  LINK = 'link',
}

// TODO: our current enum naming convention: PascalCase singular name and PascalCase keys.
//  ex: enum SettingItemType { Header = 'header', ... }
//  rename all that is named differently.
export enum SettingItemTypes {
  HEADER = 'header',
  MARKDOWN = 'markdown',
  TEXTBOX = 'textBox',
  SELECT = 'selectionList',
  LOCALE_SELECT = 'localeSelect',
  WIDGET = 'widget',
  CHECKBOX = 'checkBox',
}

export enum WidgetType {
  Subscription = 'subscription',
  PaymentMethod = 'paymentMethod',
}

export enum SettingsActionType {
  NAVIGATE = 'navigate',
  MANAGE_SUBSCRIPTION = 'manageSubscription',
  EDIT = 'settingsEdit',
}

export enum SettingsSubmitType {
  UPDATE_PROFILE = 'updateProfile',
  UPDATE_PASSWORD = 'updatePassword',
}

export enum horizontalDirection {
  LEFT = 'left',
  RIGHT = 'right',
}

export enum HttpVerb {
  Get = 'GET',
  Post = 'POST',
  Put = 'PUT',
  Delete = 'DELETE',
}

export enum HttpHeader {
  ContentType = 'Content-Type',
  ContentLength = 'Content-Length',
  DeviceInfo = 'X-Device-Info',
}

/**
 * Few words about response headers and case sensitivity:
 * Headers are in general case insensitive, but when headers are put into the object
 * then by default they start to be case sensitive. axios fixes that by forcing them
 * to be lowercase, as they are converted into object. This means we need to make sure
 * we use lower cased header names in our code.
 **/

/** List of supported response headers. Please note that response headers must be lower cased */
export enum ResponseHttpHeader {
  AuthenticationChange = 'x-authentication-change',
  AuthorizationError = 'x-authorization-error',
  AccessRestricted = 'x-access-restricted',
}

export enum ContentType {
  Json = 'application/json',
}

export enum ResponseType {
  Basic = 'basic',
  Cors = 'cors',
  Error = 'error',
  Opaque = 'opaque',
  OpaqueRedirect = 'opaqueredirect',
}
